import Configuration from '@/agilis/util/Configuration'
import { i18n } from '@/i18n/i18n'
import jsonLogic from '@/plugins/jsonLogic'
import L from 'lodash'

// the DataTableGroupingMixin adds the ablity to group rows in a DataTable
// and to calculate aggregate metrics on specific columns within each group.
// the data set must be structured so that rows are already grouped by
// a grouping column.
//
// for example, a column cost can be aggregated into a total cost for each
// product type (the grouping column):
//
// product type       cost
// -----------------------
// flower               10
// flower               20
//              total   30
// -----------------------
// tree                110
// tree                 40
//              total  150
// -----------------------
//              total  180
//
// - the dataTable "groupingColumn" property indicates the column to use to group the table.
//
// eg.:
// "datatable": {
//     ...
//     "groupingColumn": "groupId",
//     "columns": {
//         ...
//     }
// }
//
// - the column "grouping" property contains properties that describe the calculations to perform on the column values
//   any calculated column can have a different value for each item and group of the table and also a value for the whole table
// - the "$levelName$" [item, group, table] property contains the calculations to perform on the level specified by the property name
//   the calculation result will be stored at the specified level
// - the "type" property affects how the value will be rendered
// - the "renderMethod" property specifies the method to launch for rendering the column value
// - the "jsonLogic" property describes the calculation to perform
//   and can refer to any column value present on the current item, group or on the table
// - the "label" property contains the label to concatenate to the value when rendered
//
// eg.:
// "cost": {
//     "id": "cost",
//     "type": "number",
//     "grouping": {
//         "group": {
//             "id": "cost",
//             "type": "text",
//             "renderMethod": "renderCurrency", // the result is formatted as currency
//             "jsonLogic": { // sum the current group cost and the current item cost
//                            // and the result will be stored as the new cost of the current group
//                 "+": [
//                     { "var": "group.cost" },
//                     { "var": "item.cost" }
//                 ]
//             }
//         },
//         "table": {
//             "id": "cost",
//             "type": "text",
//             "lable": "arpa.label.total",
//             "renderMethod": "renderCurrency", // the result is formatted as currency
//             "jsonLogic": { // sum the current item cost and the table cost
//                            // and the result will be stored as the new cost of the table
//                 "+": [
//                     { "var": "item.cost" },
//                     { "var": "table.cost" }
//                 ]
//             }
//         }
//     }
// },
// "percentage": {
//     "id": "percentage",
//     "type": "number",
//     "grouping": {
//         "item": {
//             "id": "percentage",
//             "type": "number",
//             "format": "percentage", // the result is formatted as percentage
//             "jsonLogic": { // calculate how much the current item cost is as a percentage of the current group cost
//                            // and the result will be stored as the new percentage of the current item
//                 "*": [
//                     {
//                         "/": [
//                             { "var": "item.cost" },
//                             { "var": "group.cost" }
//                         ]
//                     },
//                     100
//                 ]
//             }
//         },
//         "group": {
//             "id": "percentage",
//             "type": "number",
//             "format": "percentage", // the result is formatted as percentage
//             "jsonLogic": { // calculate how much the current group cost is as a percentage of the table cost
//                            // and the result will be stored as the new percentage of the current group
//                 "*": [
//                     {
//                         "/": [
//                             { "var": "group.cost" },
//                             { "var": "table.cost" }
//                         ]
//                     },
//                     100
//                 ]
//             }
//         }
//     }
// }

export default {
  name: 'DataTableGroupingMixin',

  props: {
    integratedData: Object
  },

  data () {
    return {
      groupingColumnId: null,
      calculatingColumns: [],
      groupData: {
        items: {},
        groups: {},
        table: {}
      },
      jsonLogicContext: {
        item: {},
        group: {},
        table: {},
        integratedData: {}
      }
    }
  },

  mounted () {
    this.jsonLogicContext.integratedData = this.integratedData
  },

  watch: {
    items: {
      deep: true,
      handler () {
        this.tableDataGrouping()
      }
    },
    integratedData: {
      handler (newValue) {
        this.jsonLogicContext.integratedData = newValue
        this.tableDataGrouping()
      },
      deep: true
    }
  },

  methods: {
    setGroupingConfiguration (dataTable) {
      this.groupingColumnId = dataTable?.grouping?.column
      this.calculatingColumns = []
      if (dataTable?.columns) {
        for (const column of Object.values(dataTable.columns)) {
          if (column.grouping) {
            this.calculatingColumns.push(column)
          }
        }
      }
    },

    tableDataGrouping () {
      this.clearGroupData()
      if (this.items && this.items.length > 0) {
        this.calculatingColumns.forEach(column => {
          this.columnCalculation(column)
        })
      }
      this.$emit('group-data-update', this.groupData)
    },

    columnCalculation (column) {
      this.items.forEach(item => {
        this.itemCalculation(column, item)
      })
    },

    itemCalculation (column, item) {
      if (column.grouping) {
        this.initGroupData(item, column)

        const groupId = this.getGroupId(item)

        this.jsonLogicContext.item = this.groupData.items[item.id]
        this.jsonLogicContext.group = this.groupData.groups[groupId]
        this.jsonLogicContext.table = this.groupData.table

        Object.keys(column.grouping).forEach(groupDataContext => {
          if (this.jsonLogicContext[groupDataContext]) {
            this.contextCalculation(groupDataContext, column)
          }
        })

        this.updateDataGroup()
      }
    },

    contextCalculation (groupDataContext, column) {
      const logic = column.grouping[groupDataContext].jsonLogic
      if (logic) {
        const columnId = column.grouping[groupDataContext].id
        const result = jsonLogic.apply(logic, this.jsonLogicContext)
        this.jsonLogicContext[groupDataContext][columnId] = result
      }
    },

    renderComputedItemColumn (itemId, colId) {
      const colConfig = this.getCalculatingColumn(colId)
      const groupingColConfig = colConfig?.grouping?.item || this.configuration.columns[colId]
      const item = this.getItemData(itemId)
      return this.renderColumnWithLabel(item, groupingColConfig, true)
    },

    renderComputedGroupColumn (groupId, colId) {
      const colConfig = this.getCalculatingColumn(colId)
      const groupingColConfig = colConfig?.grouping?.group
      const group = this.getGroupData(groupId)
      return this.renderColumnWithLabel(group, groupingColConfig)
    },

    renderComputedTableColumn (colId) {
      const colConfig = this.getCalculatingColumn(colId)
      const groupingColConfig = colConfig?.grouping?.table
      const table = this.getTableData()
      return this.renderColumnWithLabel(table, groupingColConfig)
    },

    renderColumnWithLabel (item, colConfig) {
      let result = ''
      if (colConfig) {
        if (item?.[colConfig.id] !== undefined) {
          result = Configuration.datatable.renderColumn(colConfig, item)
        }

        if (colConfig.concatLabel) {
          result = `${i18n.t(colConfig.concatLabel)} ${result}`
        }
      }

      return result
    },

    clearGroupData () {
      this.groupData.items = {}
      this.groupData.groups = {}
      this.groupData.table = {}
    },

    initGroupData (item, column) {
      const groupId = this.getGroupId(item)
      const columnId = column.id

      if (!this.groupData.items[item.id]) {
        this.groupData.items[item.id] = L.cloneDeep(item)
      }

      if (column.grouping.item?.jsonLogic) {
        if (!this.groupData.items[item.id][columnId]) {
          this.groupData.items[item.id][columnId] = this.getInitValue(column.grouping.item)
        }
      }

      if (column.grouping.group?.jsonLogic) {
        if (!this.groupData.groups[groupId]) {
          this.groupData.groups[groupId] = {}
        }
        if (!this.groupData.groups[groupId][columnId]) {
          this.groupData.groups[groupId][columnId] = this.getInitValue(column.grouping.group)
        }
      }

      if (column.grouping.table?.jsonLogic) {
        if (!this.groupData.table[columnId]) {
          this.groupData.table[columnId] = this.getInitValue(column.grouping.table)
        }
      }
    },

    updateDataGroup () {
      const item = this.jsonLogicContext.item
      const groupId = this.getGroupId(item)

      this.groupData.items[item.id] = this.jsonLogicContext.item
      this.groupData.groups[groupId] = this.jsonLogicContext.group
      this.groupData.table = this.jsonLogicContext.table
    },

    getItemData (itemId, columnId) {
      if (columnId) {
        return this.groupData.items[itemId]?.[columnId]
      } else {
        return this.groupData.items[itemId]
      }
    },

    getGroupData (groupId, columnId) {
      if (columnId) {
        return this.groupData.groups[groupId]?.[columnId]
      } else {
        return this.groupData.groups[groupId]
      }
    },

    getTableData (columnId) {
      if (columnId) {
        return this.groupData.table?.[columnId]
      } else {
        return this.groupData.table
      }
    },

    getGroupId (item) {
      return item[this.groupingColumnId]
    },

    getCalculatingColumn (colId) {
      return this.calculatingColumns.find(column => column.id === colId)
    },

    getInitValue (column) {
      switch (column?.type) {
        case 'date':
          return null
        default:
          return 0
      }
    }
  }
}
